import React, { useCallback, useEffect, useState } from "react";
import {
    Users, UserPlus, Mail, ShieldCheck, Loader2, Copy, Check, Trash2,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";

const PERMISSION_LABELS = {
    owner:          { label: "Owner",          desc: "Full access — billing, org settings, everything." },
    admin:          { label: "Admin",          desc: "Manage jobs, candidates, and team members." },
    recruiter:      { label: "Recruiter",      desc: "Create jobs, review Talent Passports, message candidates." },
    hiring_manager: { label: "Hiring Manager", desc: "View assigned jobs, comment, interview candidates." },
    interviewer:   { label: "Interviewer",    desc: "View candidates and leave interview feedback." },
};

const PillPerm = ({ perm }) => {
    const tones = {
        owner: "bg-emerald-50 text-emerald-800 border-emerald-200",
        admin: "bg-sky-50 text-sky-800 border-sky-200",
        recruiter: "bg-violet-50 text-violet-800 border-violet-200",
        hiring_manager: "bg-amber-50 text-amber-900 border-amber-200",
        interviewer: "bg-slate-50 text-slate-700 border-slate-200",
    };
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[perm] || tones.interviewer}`}>
            {PERMISSION_LABELS[perm]?.label || perm}
        </span>
    );
};

export default function EmployerTeam() {
    const { user } = useAuth();
    const canManage = hasCapability(user, "manage_team");

    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [showInvite, setShowInvite] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [m, i] = await Promise.all([
                api.get("/employer/team/members"),
                canManage ? api.get("/employer/team/invites") : Promise.resolve({ data: [] }),
            ]);
            setMembers(m.data || []);
            setInvites(i.data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [canManage]);

    useEffect(() => { load(); }, [load]);

    const changePermission = async (m, newPerm) => {
        if (newPerm === m.permission) return;
        setBusyId(m.user_id);
        try {
            await api.patch(`/employer/team/members/${m.user_id}/permission`, { permission: newPerm });
            toast.success(`Updated ${m.email} to ${PERMISSION_LABELS[newPerm].label}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const removeMember = async (m) => {
        if (!window.confirm(`Remove ${m.email} from the team? They'll be signed out.`)) return;
        setBusyId(m.user_id);
        try {
            await api.delete(`/employer/team/members/${m.user_id}`);
            toast.success(`Removed ${m.email}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const revokeInvite = async (inv) => {
        setBusyId(inv.id);
        try {
            await api.delete(`/employer/team/invites/${inv.id}`);
            toast.success("Invite revoked");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="employer-team-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Employer</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Team
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Invite people to your workspace and assign the permission they need.
                    </p>
                </div>
                {canManage && (
                    <button
                        onClick={() => setShowInvite((s) => !s)}
                        data-testid="team-invite-toggle"
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                        <UserPlus className="h-4 w-4" /> Invite teammate
                    </button>
                )}
            </div>

            {showInvite && canManage && (
                <InviteForm
                    onCreated={async () => { setShowInvite(false); await load(); }}
                />
            )}

            {/* Members */}
            <section className="mt-8">
                <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">Team members</h2>
                <div className="mt-3 rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                    {loading ? (
                        <div className="py-16 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-emerald-600" /></div>
                    ) : members.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">
                            No team members yet.
                        </div>
                    ) : (
                        <table className="w-full text-sm" data-testid="team-members-table">
                            <thead>
                                <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="text-left px-4 py-2">Member</th>
                                    <th className="text-left px-4 py-2">Permission</th>
                                    <th className="text-right px-4 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.map((m) => (
                                    <tr key={m.user_id} data-testid={`team-row-${m.user_id}`} className="border-b border-border last:border-0">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-foreground">{m.name || m.email}</div>
                                            <div className="text-xs text-muted-foreground">{m.email}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {m.is_owner ? (
                                                <PillPerm perm="owner" />
                                            ) : canManage ? (
                                                <select
                                                    value={m.permission}
                                                    onChange={(e) => changePermission(m, e.target.value)}
                                                    disabled={busyId === m.user_id}
                                                    data-testid={`team-perm-select-${m.user_id}`}
                                                    className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                                                >
                                                    {Object.entries(PERMISSION_LABELS)
                                                        .filter(([k]) => k !== "owner")
                                                        .map(([k, v]) => (
                                                            <option key={k} value={k}>{v.label}</option>
                                                        ))}
                                                </select>
                                            ) : (
                                                <PillPerm perm={m.permission} />
                                            )}
                                            <div className="mt-1 text-[11px] text-muted-foreground">
                                                {PERMISSION_LABELS[m.permission]?.desc}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {!m.is_owner && canManage && (
                                                <button
                                                    onClick={() => removeMember(m)}
                                                    disabled={busyId === m.user_id}
                                                    data-testid={`team-remove-${m.user_id}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-50"
                                                >
                                                    <Trash2 className="h-3 w-3" /> Remove
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </section>

            {/* Pending invites */}
            {canManage && invites.length > 0 && (
                <section className="mt-8">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">Pending invites</h2>
                    <div className="mt-3 space-y-2">
                        {invites.filter((i) => i.status === "pending").map((inv) => (
                            <div
                                key={inv.id}
                                data-testid={`team-invite-${inv.id}`}
                                className="rounded-xl border border-border bg-card px-4 py-3 flex items-center justify-between gap-3"
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span className="font-medium text-foreground">{inv.email}</span>
                                        <PillPerm perm={inv.permission || "hiring_manager"} />
                                    </div>
                                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                                        <span>Invite link:</span>
                                        <code className="rounded bg-muted px-1.5 py-0.5">{`/team/accept/${inv.token}`}</code>
                                        <CopyBtn value={`${window.location.origin}/team/accept/${inv.token}`} />
                                    </div>
                                </div>
                                <button
                                    onClick={() => revokeInvite(inv)}
                                    disabled={busyId === inv.id}
                                    data-testid={`team-invite-revoke-${inv.id}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                                >
                                    Revoke
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function InviteForm({ onCreated }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [permission, setPermission] = useState("hiring_manager");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/employer/team/invites", { email, name, permission });
            toast.success(`Invited ${email} as ${PERMISSION_LABELS[permission].label}`);
            setEmail(""); setName("");
            onCreated?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="team-invite-form"
            className="mt-4 rounded-2xl border border-dashed border-border bg-card p-5"
        >
            <h3 className="text-sm font-semibold text-foreground">Invite a teammate</h3>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                    required type="email" placeholder="work.email@yourco.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="team-invite-email"
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <input
                    type="text" placeholder="Full name (optional)"
                    value={name} onChange={(e) => setName(e.target.value)}
                    data-testid="team-invite-name"
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
                <select
                    value={permission} onChange={(e) => setPermission(e.target.value)}
                    data-testid="team-invite-permission"
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                    {Object.entries(PERMISSION_LABELS)
                        .filter(([k]) => k !== "owner")
                        .map(([k, v]) => (
                            <option key={k} value={k}>{v.label} — {v.desc}</option>
                        ))}
                </select>
            </div>
            <div className="mt-3 flex items-center justify-end">
                <button
                    type="submit" disabled={busy}
                    data-testid="team-invite-submit"
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Create invite
                </button>
            </div>
        </form>
    );
}

function CopyBtn({ value }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(value);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                } catch { /* ignore */ }
            }}
            className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] hover:bg-muted"
        >
            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}
